import * as React from 'react';

import type { HiveWorkingBranch } from '@gitbook/hive-client';
import type { RevisionPageDocument, RevisionPageMetaLink } from '@gitbook/hive-core';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    Description,
    Label,
    LabeledContainer,
    LabeledContainerBody,
    LabeledContainerHeader,
    Separator,
} from '@gitbook/spine-react';
import { assertIsDefined } from '@gitbook/util-ts';

import { ContentReferenceContextProvider, ContentReferenceInput } from '../../../../Components';
import type { PageOptionsTabProps } from './types';

/**
 * Tab for managing the page metadata
 */
export function PageOptionsTabMetadata(props: PageOptionsTabProps): React.ReactElement {
    const { screen, page, branch } = props;
    const organizationId = screen.organization;
    assertIsDefined(organizationId, 'Expected organizationId to be defined');

    return (
        <ContentReferenceContextProvider
            value={{
                organization: organizationId,
                space: screen.space,
            }}
        >
            <PageMetaLinkCanonical page={page} branch={branch} />
            <Separator />
            <PageMetaLinkAlternates page={page} branch={branch} />
        </ContentReferenceContextProvider>
    );
}

/**
 * Canonical link setting for a page.
 */
function PageMetaLinkCanonical(props: { page: RevisionPageDocument; branch: HiveWorkingBranch }) {
    const { page, branch } = props;

    return (
        <LabeledContainer>
            <LabeledContainerHeader>
                <Label>Canonical</Label>
                <Description>
                    Specify a canonical reference for this page to indicate the preferred version of
                    the content.
                </Description>
            </LabeledContainerHeader>
            <LabeledContainerBody>
                <PageMetaLinkPicker
                    link={page.metaLinks?.canonical ?? null}
                    onChange={(link) => {
                        branch.change((change) => {
                            change.updatePageCanonicalLink(page.id, link);
                        });
                    }}
                />
            </LabeledContainerBody>
        </LabeledContainer>
    );
}

/**
 * Alternates link setting for a page.
 */
function PageMetaLinkAlternates(props: { page: RevisionPageDocument; branch: HiveWorkingBranch }) {
    const { page, branch } = props;
    const [addNew, setAddNew] = React.useState(false);

    return (
        <LabeledContainer>
            <LabeledContainerHeader>
                <div className="mb-1 flex items-center justify-between">
                    <Label>Alternates</Label>
                    <Button
                        onClick={() => setAddNew(true)}
                        iconOnly
                        size="xsmall"
                        kind="secondary"
                        leadingIcon={Icon.Plus}
                        label="Add"
                    >
                        Add alternate
                    </Button>
                </div>
                <Description>
                    Optionally specify alternates for this page to help indicate different versions
                    of the same content.
                </Description>
            </LabeledContainerHeader>
            <LabeledContainerBody>
                {page.metaLinks?.alternates?.map((alternate, index) => (
                    <div key={index} className="mb-1">
                        <PageMetaLinkPicker
                            link={alternate}
                            onChange={(link) => {
                                branch.change((change) => {
                                    change.updatePageAlternateLink(page.id, index, link);
                                });
                            }}
                        />
                    </div>
                ))}
                {addNew ? (
                    <PageMetaLinkPicker
                        link={null}
                        onChange={(link) => {
                            if (!link) {
                                return;
                            }
                            branch.change((change) => {
                                change.addPageAlternateLink(page.id, link);
                            });
                            setAddNew(false);
                        }}
                    />
                ) : null}
            </LabeledContainerBody>
        </LabeledContainer>
    );
}

/**
 * Picker for a page meta link.
 * Supports picking a page from any space or entering a URL.
 */
function PageMetaLinkPicker(props: {
    link: RevisionPageMetaLink | null;
    onChange: (link: RevisionPageMetaLink | null) => void;
}) {
    const { link, onChange } = props;
    return (
        <div className="flex gap-2">
            <div className="flex-1">
                <ContentReferenceInput
                    placeholder="Choose a page or type an URL..."
                    value={link}
                    onChange={(contentRef) => {
                        if (contentRef && contentRef.kind !== 'url' && contentRef.kind !== 'page')
                            return;
                        onChange(contentRef);
                    }}
                    size="medium"
                    clearable={true}
                    accept={['space', 'page', 'url']}
                />
            </div>
            {link ? (
                <Button
                    kind="secondary"
                    size="medium"
                    iconOnly
                    leadingIcon={Icon.Trashcan}
                    label="Clear"
                    onClick={() => {
                        onChange(null);
                    }}
                />
            ) : null}
        </div>
    );
}
